// buildingCodes.ts
// Pure data/logic module — no React.
// Exports: STATE_DEFAULT_CZ, COUNTY_CZ_OVERRIDES, STATE_CODES, IECC_ENVELOPE,
//          IECCEnvelopeRow (interface), getClimateZone()

// ---------------------------------------------------------------------------
// IECC Climate Zone interface
// ---------------------------------------------------------------------------
export interface IECCEnvelopeRow {
  fenestrationU: string;
  skylightU: string;
  shgc: string;
  ceilingR: string;
  wfWallR: string;
  massWallR: string;
  floorR: string;
  basementR: string;
  slabR: string;
  crawlR: string;
}

// ---------------------------------------------------------------------------
// Default IECC climate zone per US state (2-letter abbreviation)
// ---------------------------------------------------------------------------
export const STATE_DEFAULT_CZ: Record<string, string> = {
  AK: "7",
  AL: "3A",
  AR: "3A",
  AZ: "3B",
  CA: "3B",
  CO: "5B",
  CT: "5A",
  DC: "4A",
  DE: "4A",
  FL: "2A",
  GA: "3A",
  HI: "1A",
  IA: "5A",
  ID: "5B",
  IL: "5A",
  IN: "5A",
  KS: "4A",
  KY: "4A",
  LA: "2A",
  MA: "5A",
  MD: "4A",
  ME: "6A",
  MI: "5A",
  MN: "6A",
  MO: "4A",
  MS: "3A",
  MT: "6B",
  NC: "3A",
  ND: "7",
  NE: "5A",
  NH: "6A",
  NJ: "4A",
  NM: "4B",
  NV: "5B",
  NY: "5A",
  OH: "5A",
  OK: "4A",
  OR: "4C",
  PA: "5A",
  RI: "5A",
  SC: "3A",
  SD: "7",
  TN: "4A",
  TX: "3A",
  UT: "5B",
  VA: "4A",
  VT: "7",
  WA: "4C",
  WI: "6A",
  WV: "5A",
  WY: "6B",
};

// ---------------------------------------------------------------------------
// County-level overrides (only counties that differ from state default)
// Keys: LOWERCASE county names, no "County"/"Parish"/"Borough" suffix
// ---------------------------------------------------------------------------
export const COUNTY_CZ_OVERRIDES: Record<string, Record<string, string>> = {
  AL: {
    // default 3A; northern counties = 4A
    colbert: "4A",
    cullman: "4A",
    dekalb: "4A",
    franklin: "4A",
    jackson: "4A",
    lauderdale: "4A",
    lawrence: "4A",
    limestone: "4A",
    madison: "4A",
    marshall: "4A",
    morgan: "4A",
    winston: "4A",
  },
  AR: {
    // default 3A; northern tier = 4A
    baxter: "4A",
    benton: "4A",
    boone: "4A",
    carroll: "4A",
    cleburne: "4A",
    conway: "4A",
    faulkner: "4A",
    fulton: "4A",
    independence: "4A",
    izard: "4A",
    johnson: "4A",
    lawrence: "4A",
    logan: "4A",
    madison: "4A",
    marion: "4A",
    newton: "4A",
    pope: "4A",
    randolph: "4A",
    scott: "4A",
    searcy: "4A",
    sharp: "4A",
    stone: "4A",
    "van buren": "4A",
    washington: "4A",
    white: "4A",
    yell: "4A",
  },
  AZ: {
    // default 3B (central)
    maricopa: "2B",
    pima: "2B",
    pinal: "2B",
    "santa cruz": "2B",
    yuma: "2B",
    "la paz": "2B",
    graham: "3B",
    greenlee: "3B",
    mohave: "3B",
    yavapai: "3B",
    gila: "3B",
    navajo: "4B",
    coconino: "5B",
    apache: "5B",
  },
  CA: {
    // default 3B (inland)
    imperial: "2B",
    "los angeles": "3B",
    orange: "3B",
    "san diego": "3B",
    riverside: "3B",
    "san bernardino": "3B",
    kern: "3B",
    fresno: "3B",
    tulare: "3B",
    kings: "3B",
    madera: "3B",
    merced: "3B",
    stanislaus: "3B",
    "san joaquin": "3B",
    alameda: "3C",
    "contra costa": "3C",
    solano: "3C",
    napa: "3C",
    sonoma: "3C",
    marin: "3C",
    "san francisco": "3C",
    "san mateo": "3C",
    "santa cruz": "3C",
    monterey: "3C",
    "san luis obispo": "3C",
    "santa barbara": "3C",
    ventura: "3C",
    "san benito": "3C",
    "santa clara": "3C",
    lake: "3C",
    butte: "4B",
    tehama: "4B",
    colusa: "4B",
    glenn: "4B",
    sutter: "4B",
    yuba: "4B",
    sacramento: "4B",
    yolo: "4B",
    amador: "4B",
    calaveras: "4B",
    mariposa: "4B",
    placer: "4B",
    "el dorado": "5B",
    nevada: "5B",
    tuolumne: "5B",
    mono: "5B",
    "del norte": "4C",
    humboldt: "4C",
    mendocino: "4C",
    shasta: "5B",
    trinity: "5B",
    siskiyou: "6B",
    modoc: "6B",
    lassen: "6B",
    plumas: "6B",
    sierra: "6B",
    alpine: "6B",
    inyo: "4B",
  },
  CO: {
    // default 5B; high mountains = 6B/7; eastern plains = 4B
    baca: "4B",
    prowers: "4B",
    bent: "4B",
    otero: "4B",
    crowley: "4B",
    kiowa: "4B",
    "las animas": "4B",
    huerfano: "4B",
    teller: "6B",
    park: "6B",
    "clear creek": "6B",
    gilpin: "6B",
    summit: "7",
    pitkin: "7",
    gunnison: "7",
    saguache: "7",
    mineral: "7",
    hinsdale: "7",
    "san juan": "7",
    ouray: "6B",
    grand: "7",
    routt: "7",
    jackson: "7",
    moffat: "6B",
    "cache la poudre": "5B",
    larimer: "5B",
    boulder: "5B",
    alamosa: "6B",
    conejos: "6B",
    costilla: "6B",
    "rio grande": "6B",
    archuleta: "5B",
    "la plata": "5B",
    montezuma: "5B",
    dolores: "5B",
  },
  FL: {
    // default 2A
    "miami-dade": "1A",
    monroe: "1A",
    broward: "2A",
    "palm beach": "2A",
  },
  GA: {
    // default 3A; north Georgia mountains = 4A/5A
    union: "5A",
    towns: "5A",
    fannin: "5A",
    rabun: "4A",
    habersham: "4A",
    white: "4A",
    lumpkin: "4A",
    dawson: "4A",
    pickens: "4A",
    gilmer: "4A",
    murray: "4A",
    walker: "4A",
    dade: "4A",
    catoosa: "4A",
    whitfield: "4A",
    gordon: "4A",
    bartow: "4A",
    cherokee: "4A",
    forsyth: "4A",
    hall: "4A",
    banks: "4A",
    jackson: "4A",
    madison: "4A",
    elbert: "4A",
    hart: "4A",
    franklin: "4A",
    stephens: "4A",
  },
  ID: {
    // default 5B
    benewah: "6B",
    bonner: "6B",
    boundary: "6B",
    clearwater: "6B",
    idaho: "6B",
    kootenai: "6B",
    latah: "6B",
    lewis: "5B",
    shoshone: "6B",
    blaine: "6B",
    camas: "6B",
    bannock: "6B",
    caribou: "6B",
    "bear lake": "6B",
    bingham: "6B",
    butte: "7",
    clark: "7",
    custer: "7",
    fremont: "7",
    jefferson: "6B",
    lemhi: "7",
    madison: "7",
    teton: "7",
    bonneville: "6B",
  },
  IL: {
    // default 5A; southern IL = 4A
    alexander: "4A",
    bond: "4A",
    calhoun: "4A",
    clark: "4A",
    clay: "4A",
    clinton: "4A",
    crawford: "4A",
    cumberland: "4A",
    edwards: "4A",
    effingham: "4A",
    fayette: "4A",
    franklin: "4A",
    gallatin: "4A",
    hamilton: "4A",
    hardin: "4A",
    jackson: "4A",
    jasper: "4A",
    jefferson: "4A",
    jersey: "4A",
    johnson: "4A",
    lawrence: "4A",
    madison: "4A",
    marion: "4A",
    massac: "4A",
    monroe: "4A",
    perry: "4A",
    pope: "4A",
    pulaski: "4A",
    randolph: "4A",
    richland: "4A",
    saline: "4A",
    "st. clair": "4A",
    union: "4A",
    wabash: "4A",
    washington: "4A",
    wayne: "4A",
    white: "4A",
    williamson: "4A",
  },
  IN: {
    // default 5A; southern IN = 4A
    clark: "4A",
    crawford: "4A",
    daviess: "4A",
    dubois: "4A",
    floyd: "4A",
    gibson: "4A",
    harrison: "4A",
    jackson: "4A",
    jefferson: "4A",
    jennings: "4A",
    knox: "4A",
    lawrence: "4A",
    martin: "4A",
    ohio: "4A",
    orange: "4A",
    perry: "4A",
    pike: "4A",
    posey: "4A",
    ripley: "4A",
    scott: "4A",
    spencer: "4A",
    switzerland: "4A",
    vanderburgh: "4A",
    warrick: "4A",
    washington: "4A",
  },
  KS: {
    // default 4A; western KS = 5A
    cheyenne: "5A",
    rawlins: "5A",
    decatur: "5A",
    norton: "5A",
    phillips: "5A",
    smith: "5A",
    jewell: "5A",
    republic: "5A",
    sherman: "5A",
    thomas: "5A",
    sheridan: "5A",
    graham: "5A",
    rooks: "5A",
    osborne: "5A",
    mitchell: "5A",
    cloud: "5A",
    wallace: "5A",
    logan: "5A",
    gove: "5A",
    trego: "5A",
    ellis: "5A",
    russell: "5A",
    lincoln: "5A",
    ottawa: "5A",
    wichita: "5A",
    scott: "5A",
    lane: "5A",
    ness: "5A",
    rush: "5A",
    finney: "5A",
    hodgeman: "5A",
    pawnee: "5A",
    ford: "5A",
    grant: "5A",
    haskell: "5A",
    gray: "5A",
    edwards: "5A",
    stanton: "5A",
    hamilton: "5A",
    kearny: "5A",
    barton: "5A",
    ellsworth: "5A",
  },
  LA: {
    // default 2A; northern parishes = 3A
    bossier: "3A",
    caddo: "3A",
    claiborne: "3A",
    "de soto": "3A",
    lincoln: "3A",
    morehouse: "3A",
    natchitoches: "3A",
    "red river": "3A",
    sabine: "3A",
    union: "3A",
    webster: "3A",
    winn: "3A",
    bienville: "3A",
    jackson: "3A",
  },
  MD: {
    // default 4A; western mountain counties = 5A
    garrett: "5A",
    allegany: "5A",
  },
  ME: {
    // default 6A; inland/northern = 7
    oxford: "7",
    franklin: "7",
    somerset: "7",
    piscataquis: "7",
    penobscot: "7",
    aroostook: "7",
  },
  MI: {
    // default 5A; Upper Peninsula = 6A-7
    alger: "7",
    baraga: "7",
    chippewa: "7",
    dickinson: "7",
    gogebic: "7",
    houghton: "7",
    iron: "7",
    keweenaw: "7",
    luce: "7",
    mackinac: "7",
    marquette: "7",
    ontonagon: "7",
    schoolcraft: "7",
    delta: "6A",
    menominee: "6A",
    cheboygan: "6A",
    emmet: "6A",
    charlevoix: "6A",
    antrim: "6A",
    otsego: "6A",
    montmorency: "6A",
    alpena: "6A",
    "presque isle": "6A",
  },
  MN: {
    // default 6A; northern MN = 7
    cook: "7",
    lake: "7",
    "st. louis": "7",
    koochiching: "7",
    itasca: "7",
    beltrami: "7",
    "lake of the woods": "7",
    roseau: "7",
    marshall: "7",
    pennington: "7",
    "red lake": "7",
    polk: "7",
    norman: "7",
    mahnomen: "7",
    clearwater: "7",
    hubbard: "7",
    cass: "7",
    "crow wing": "7",
    aitkin: "7",
    carlton: "7",
    todd: "7",
    "otter tail": "7",
    becker: "7",
    clay: "7",
    wilkin: "7",
    wadena: "7",
    morrison: "7",
    "mille lacs": "7",
    kanabec: "7",
  },
  MO: {
    // default 4A; NW = 5A; extreme south Ozarks = 3A
    adair: "5A",
    clark: "5A",
    scotland: "5A",
    schuyler: "5A",
    putnam: "5A",
    sullivan: "5A",
    grundy: "5A",
    mercer: "5A",
    harrison: "5A",
    worth: "5A",
    gentry: "5A",
    atchison: "5A",
    nodaway: "5A",
    holt: "5A",
    andrew: "5A",
    buchanan: "5A",
    dekalb: "5A",
    daviess: "5A",
    caldwell: "5A",
    linn: "5A",
    macon: "5A",
    shelby: "5A",
    lewis: "5A",
    marion: "5A",
    ozark: "3A",
    taney: "3A",
    stone: "3A",
    barry: "3A",
    mcdonald: "3A",
    newton: "3A",
  },
  NC: {
    // default 3A (coastal); piedmont = 4A; mountains = 5A
    // Piedmont/central
    wake: "4A",
    durham: "4A",
    orange: "4A",
    chatham: "4A",
    alamance: "4A",
    guilford: "4A",
    forsyth: "4A",
    davidson: "4A",
    randolph: "4A",
    montgomery: "4A",
    iredell: "4A",
    alexander: "4A",
    catawba: "4A",
    lincoln: "4A",
    gaston: "4A",
    mecklenburg: "4A",
    cabarrus: "4A",
    rowan: "4A",
    stanly: "4A",
    union: "4A",
    anson: "4A",
    richmond: "4A",
    rockingham: "4A",
    caswell: "4A",
    person: "4A",
    granville: "4A",
    vance: "4A",
    warren: "4A",
    franklin: "4A",
    stokes: "4A",
    surry: "4A",
    wilkes: "4A",
    caldwell: "4A",
    burke: "4A",
    mcdowell: "4A",
    henderson: "4A",
    transylvania: "4A",
    buncombe: "4A",
    haywood: "4A",
    jackson: "4A",
    macon: "4A",
    swain: "4A",
    // Mountains (5A)
    clay: "5A",
    cherokee: "5A",
    graham: "5A",
    madison: "5A",
    yancey: "5A",
    mitchell: "5A",
    avery: "5A",
    watauga: "5A",
    ashe: "5A",
    alleghany: "5A",
  },
  NE: {
    // default 5A; panhandle = 5B
    cheyenne: "5B",
    banner: "5B",
    kimball: "5B",
    deuel: "5B",
    garden: "5B",
    sheridan: "5B",
    "box butte": "5B",
    morrill: "5B",
    "scotts bluff": "5B",
    sioux: "5B",
    dawes: "5B",
  },
  NH: {
    // default 6A; far north = 7
    carroll: "7",
    grafton: "7",
    coos: "7",
  },
  NJ: {
    // default 4A; northern NJ = 5A
    hunterdon: "5A",
    somerset: "5A",
    morris: "5A",
    warren: "5A",
    sussex: "5A",
    bergen: "5A",
    passaic: "5A",
    essex: "5A",
  },
  NM: {
    // default 4B
    eddy: "3B",
    lea: "3B",
    chaves: "3B",
    otero: "3B",
    luna: "3B",
    hidalgo: "3B",
    grant: "3B",
    guadalupe: "3B",
    catron: "5B",
    cibola: "5B",
    mckinley: "5B",
    union: "5B",
    colfax: "5B",
    taos: "5B",
    "rio arriba": "5B",
    "los alamos": "5B",
    "san juan": "5B",
    mora: "5B",
    "santa fe": "5B",
  },
  NV: {
    // default 5B
    clark: "3B",
    nye: "4B",
    lincoln: "4B",
    esmeralda: "4B",
  },
  NY: {
    // default 5A
    // NYC metro = 4A
    "new york": "4A",
    kings: "4A",
    queens: "4A",
    bronx: "4A",
    richmond: "4A",
    nassau: "4A",
    suffolk: "4A",
    rockland: "4A",
    hudson: "4A",
    middlesex: "4A",
    // Adirondacks/North = 6A-7
    hamilton: "7",
    franklin: "7",
    "st. lawrence": "7",
    jefferson: "7",
    lewis: "7",
    essex: "7",
    clinton: "6A",
    warren: "6A",
    washington: "6A",
    fulton: "6A",
    montgomery: "6A",
    saratoga: "6A",
    schenectady: "6A",
    herkimer: "6A",
    oneida: "6A",
    oswego: "6A",
    onondaga: "6A",
    madison: "6A",
    otsego: "6A",
    chenango: "6A",
    schoharie: "6A",
    cortland: "6A",
    cayuga: "6A",
    seneca: "6A",
    wayne: "6A",
    ontario: "6A",
    niagara: "6A",
    genesee: "6A",
    wyoming: "6A",
    livingston: "6A",
    monroe: "6A",
    orleans: "6A",
    steuben: "6A",
    allegany: "6A",
    cattaraugus: "6A",
  },
  OH: {
    // default 5A; southern OH = 4A
    adams: "4A",
    brown: "4A",
    butler: "4A",
    clermont: "4A",
    clinton: "4A",
    gallia: "4A",
    hamilton: "4A",
    highland: "4A",
    jackson: "4A",
    lawrence: "4A",
    meigs: "4A",
    pike: "4A",
    scioto: "4A",
    vinton: "4A",
    warren: "4A",
  },
  OK: {
    // default 4A; southern tier = 3A
    bryan: "3A",
    choctaw: "3A",
    mccurtain: "3A",
    pushmataha: "3A",
    atoka: "3A",
    coal: "3A",
    johnston: "3A",
    marshall: "3A",
    love: "3A",
    carter: "3A",
    murray: "3A",
    garvin: "3A",
    stephens: "3A",
    jefferson: "3A",
    cotton: "3A",
    comanche: "3A",
    kiowa: "3A",
    tillman: "3A",
    jackson: "3A",
    greer: "3A",
    harmon: "3A",
  },
  OR: {
    // default 4C (west of Cascades)
    "hood river": "5B",
    wasco: "5B",
    morrow: "5B",
    umatilla: "5B",
    union: "6B",
    wallowa: "6B",
    grant: "6B",
    baker: "6B",
    harney: "6B",
    malheur: "5B",
    crook: "6B",
    deschutes: "6B",
    klamath: "6B",
    lake: "6B",
    jackson: "5B",
    josephine: "4C",
    sherman: "5B",
    gilliam: "5B",
    jefferson: "5B",
    klickitat: "5B",
    lewis: "5B",
    skamania: "5B",
  },
  PA: {
    // default 5A; southeastern = 4A
    bucks: "4A",
    chester: "4A",
    delaware: "4A",
    montgomery: "4A",
    philadelphia: "4A",
    lancaster: "4A",
    york: "4A",
    adams: "4A",
    cumberland: "4A",
    dauphin: "4A",
  },
  SC: {
    // default 3A; upstate = 4A
    greenville: "4A",
    spartanburg: "4A",
    cherokee: "4A",
    union: "4A",
    york: "4A",
    chester: "4A",
    oconee: "4A",
    pickens: "4A",
    anderson: "4A",
    laurens: "4A",
    newberry: "4A",
    fairfield: "4A",
  },
  SD: {
    // default 7; southern tier = 6A
    clay: "6A",
    union: "6A",
    lincoln: "6A",
    minnehaha: "6A",
    yankton: "6A",
    "bon homme": "6A",
    turner: "6A",
    mccook: "6A",
    lake: "6A",
    moody: "6A",
    brookings: "6A",
    "charles mix": "6A",
    douglas: "6A",
  },
  TN: {
    // default 4A; west/south = 3A; far-east mountains = 5A
    shelby: "3A",
    tipton: "3A",
    lauderdale: "3A",
    haywood: "3A",
    madison: "3A",
    chester: "3A",
    henderson: "3A",
    hardeman: "3A",
    mcnairy: "3A",
    hardin: "3A",
    wayne: "3A",
    lewis: "3A",
    lawrence: "3A",
    giles: "3A",
    lincoln: "3A",
    franklin: "3A",
    moore: "3A",
    coffee: "3A",
    // Far east mountains = 5A
    carter: "5A",
    johnson: "5A",
    unicoi: "5A",
  },
  TX: {
    // default 3A (central/north Texas)
    // South Texas = 2A
    cameron: "2A",
    hidalgo: "2A",
    starr: "2A",
    zapata: "2A",
    webb: "2A",
    maverick: "2A",
    dimmit: "2A",
    zavala: "2A",
    frio: "2A",
    lasalle: "2A",
    mcmullen: "2A",
    duval: "2A",
    "jim hogg": "2A",
    brooks: "2A",
    kennedy: "2A",
    willacy: "2A",
    "jim wells": "2A",
    kleberg: "2A",
    nueces: "2A",
    "san patricio": "2A",
    aransas: "2A",
    refugio: "2A",
    bee: "2A",
    goliad: "2A",
    victoria: "2A",
    jackson: "2A",
    matagorda: "2A",
    wharton: "2A",
    "fort bend": "2A",
    harris: "2A",
    galveston: "2A",
    brazoria: "2A",
    brazos: "2A",
    colorado: "2A",
    austin: "2A",
    waller: "2A",
    chambers: "2A",
    liberty: "2A",
    lavaca: "2A",
    gonzales: "2A",
    caldwell: "2A",
    guadalupe: "3A",
    bexar: "3A",
    // West Texas (El Paso area) = 3B
    "el paso": "3B",
    hudspeth: "3B",
    culberson: "3B",
    presidio: "3B",
    brewster: "3B",
    pecos: "3B",
    terrell: "3B",
    reeves: "3B",
    ward: "3B",
    winkler: "3B",
    loving: "3B",
    crane: "3B",
    upton: "3B",
    midland: "3B",
    ector: "3B",
    andrews: "3B",
    yoakum: "3B",
    terry: "3B",
    gaines: "3B",
    dawson: "3B",
    // Jeff Davis (high elevation) = 4B
    "jeff davis": "4B",
    // Panhandle = 4A-5A
    potter: "4A",
    randall: "4A",
    castro: "4A",
    swisher: "4A",
    floyd: "4A",
    briscoe: "4A",
    hall: "4A",
    childress: "4A",
    cottle: "4A",
    king: "4A",
    stonewall: "4A",
    "deaf smith": "4A",
    parmer: "4A",
    bailey: "4A",
    lamb: "4A",
    hale: "4A",
    wheeler: "4A",
    gray: "4A",
    donley: "4A",
    collingsworth: "4A",
    hartley: "4A",
    oldham: "4A",
    moore: "4A",
    hutchinson: "4A",
    ochiltree: "5A",
    lipscomb: "5A",
    hansford: "5A",
    sherman: "5A",
    dallam: "5A",
  },
  UT: {
    // default 5B
    washington: "3B",
    kane: "4B",
    "san juan": "4B",
    grand: "4B",
    emery: "5B",
    wayne: "5B",
    garfield: "5B",
    cache: "6B",
    rich: "6B",
    summit: "6B",
    wasatch: "6B",
    duchesne: "6B",
    daggett: "6B",
    morgan: "6B",
    uintah: "5B",
  },
  VA: {
    // default 4A; western/mountain counties = 5A
    highland: "5A",
    bath: "5A",
    alleghany: "5A",
    rockbridge: "5A",
    augusta: "5A",
    rockingham: "5A",
    shenandoah: "5A",
    page: "5A",
    warren: "5A",
    frederick: "5A",
    smyth: "5A",
    washington: "5A",
    scott: "5A",
    wise: "5A",
    dickenson: "5A",
    buchanan: "5A",
    russell: "5A",
    tazewell: "5A",
    bland: "5A",
    giles: "5A",
    montgomery: "5A",
    pulaski: "5A",
    wythe: "5A",
    grayson: "5A",
    carroll: "5A",
    floyd: "5A",
    franklin: "5A",
    patrick: "5A",
  },
  VT: {
    // default 7 — VT is actually mixed 6A/7
    windham: "6A",
    windsor: "6A",
    bennington: "6A",
  },
  WA: {
    // default 4C (west of Cascades)
    lewis: "5B",
    klickitat: "5B",
    skamania: "5B",
    "hood river": "5B",
    chelan: "5B",
    douglas: "5B",
    kittitas: "5B",
    yakima: "5B",
    grant: "5B",
    benton: "5B",
    franklin: "5B",
    "walla walla": "5B",
    columbia: "5B",
    garfield: "5B",
    asotin: "5B",
    whitman: "5B",
    adams: "5B",
    lincoln: "5B",
    spokane: "5B",
    jefferson: "4C",
    okanogan: "6B",
    ferry: "6B",
    stevens: "6B",
    "pend oreille": "6B",
  },
  WI: {
    // default 6A; far north WI = 7
    iron: "7",
    vilas: "7",
    florence: "7",
    forest: "7",
    langlade: "7",
    oneida: "7",
    price: "7",
    lincoln: "7",
    taylor: "7",
    clark: "7",
    rusk: "7",
    sawyer: "7",
    polk: "7",
    barron: "7",
    burnett: "7",
    washburn: "7",
    douglas: "7",
    bayfield: "7",
    ashland: "7",
  },
  WV: {
    // default 5A; southern lowland counties = 4A
    wayne: "4A",
    cabell: "4A",
    lincoln: "4A",
    mason: "4A",
    putnam: "4A",
    kanawha: "4A",
    jackson: "4A",
    roane: "4A",
    calhoun: "4A",
    wirt: "4A",
    wood: "4A",
    pleasants: "4A",
    ritchie: "4A",
    mingo: "4A",
    logan: "4A",
    boone: "4A",
    wyoming: "4A",
    mcdowell: "4A",
  },
  WY: {
    // default 6B; high mountains = 7
    teton: "7",
    sublette: "7",
    park: "7",
    lincoln: "7",
    carbon: "7",
    natrona: "7",
    fremont: "7",
    johnson: "7",
    sheridan: "7",
    "hot springs": "7",
    "big horn": "7",
    washakie: "7",
  },
  AK: {
    // default 7; Southeast coastal = 6A; Interior/Far North = 8
    juneau: "6A",
    sitka: "6A",
    "ketchikan gateway": "6A",
    wrangell: "6A",
    "prince of wales-hyder": "6A",
    haines: "7",
    skagway: "7",
    yakutat: "7",
    bethel: "8",
    "yukon-koyukuk": "8",
    "southeast fairbanks": "8",
    "fairbanks north star": "8",
    denali: "8",
    "north slope": "8",
    "northwest arctic": "8",
    nome: "8",
    kusilvak: "8",
    "wade hampton": "8",
    "lake and peninsula": "7",
    "bristol bay": "7",
    dillingham: "7",
    "matanuska-susitna": "7",
    "kenai peninsula": "7",
    "kodiak island": "7",
    anchorage: "7",
    "valdez-cordova": "7",
    chugach: "7",
  },
};

// ---------------------------------------------------------------------------
// State building code adoptions
// ---------------------------------------------------------------------------
export const STATE_CODES: Record<
  string,
  { ibc: string; irc: string; iecc: string; ipc: string; imc: string; notes?: string }
> = {
  AL: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  AK: { ibc: "2018 IBC", irc: "2018 IRC", iecc: "2018 IECC", ipc: "2018 IPC", imc: "2018 IMC" },
  AZ: {
    ibc: "2018 IBC",
    irc: "2018 IRC",
    iecc: "2018 IECC",
    ipc: "2018 IPC",
    imc: "2018 IMC",
    notes: "Adoption varies by jurisdiction",
  },
  AR: {
    ibc: "2012 IBC",
    irc: "2012 IRC",
    iecc: "2014 IECC",
    ipc: "2006 IPC",
    imc: "2009 IMC",
    notes: "One of the oldest adoptions in the US — verify locally",
  },
  CA: {
    ibc: "2022 CBC (2021 IBC base)",
    irc: "2022 CRC (2021 IRC base)",
    iecc: "Title 24 Part 6 (CA Energy Code)",
    ipc: "2022 CPC",
    imc: "2022 CMC",
    notes: "California uses its own codes with mandatory state energy amendments",
  },
  CO: {
    ibc: "2021 IBC",
    irc: "2021 IRC",
    iecc: "2021 IECC",
    ipc: "2021 IPC",
    imc: "2021 IMC",
    notes: "Statewide minimum — many municipalities adopt higher editions",
  },
  CT: { ibc: "2018 IBC", irc: "2018 IRC", iecc: "2021 IECC", ipc: "2018 IPC", imc: "2018 IMC" },
  DC: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  DE: { ibc: "2018 IBC", irc: "2018 IRC", iecc: "2021 IECC", ipc: "2018 IPC", imc: "2018 IMC" },
  FL: {
    ibc: "FBC 8th Ed. (2021 IBC base)",
    irc: "FBC-R 8th Ed.",
    iecc: "FBC Energy 8th Ed. (2021 IECC base)",
    ipc: "FPC 8th Ed.",
    imc: "FMC 8th Ed.",
    notes: "Florida Building Code is state-specific with Florida amendments",
  },
  GA: {
    ibc: "2018 IBC",
    irc: "2018 IRC",
    iecc: "2018 IECC",
    ipc: "2018 IPC",
    imc: "2018 IMC",
    notes: "Georgia state amendments apply",
  },
  HI: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  ID: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  IL: {
    ibc: "2021 IBC",
    irc: "2021 IRC",
    iecc: "2021 IECC",
    ipc: "2021 IPC",
    imc: "2021 IMC",
    notes: "Chicago and Cook County may use different editions",
  },
  IN: {
    ibc: "2020 IBC",
    irc: "2020 IRC",
    iecc: "2014 IECC",
    ipc: "2020 IPC",
    imc: "2020 IMC",
    notes: "Indiana residential energy code lags behind — verify locally",
  },
  IA: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  KS: {
    ibc: "2018 IBC",
    irc: "No statewide residential code",
    iecc: "2018 IECC",
    ipc: "2018 IPC",
    imc: "2018 IMC",
    notes: "Kansas has no mandatory statewide residential building code",
  },
  KY: {
    ibc: "2018 IBC",
    irc: "2018 IRC",
    iecc: "2007 IECC",
    ipc: "2018 IPC",
    imc: "2018 IMC",
    notes: "Kentucky energy code is among the oldest in the US — verify locally",
  },
  LA: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  ME: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  MD: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  MA: {
    ibc: "10th Ed. (2021 IBC base)",
    irc: "10th Ed. (2021 IRC base)",
    iecc: "2021 IECC",
    ipc: "2021 IPC",
    imc: "2021 IMC",
    notes: "Massachusetts has a stretch energy code option for municipalities",
  },
  MI: {
    ibc: "2015 IBC",
    irc: "2015 IRC",
    iecc: "2015 IECC",
    ipc: "2015 IPC",
    imc: "2015 IMC",
    notes: "Michigan code adoption is several editions behind — verify locally",
  },
  MN: {
    ibc: "2020 IBC",
    irc: "2020 IRC",
    iecc: "MN 2020 Energy Code",
    ipc: "2020 IPC",
    imc: "2020 IMC",
    notes: "Minnesota has its own State Building Code based on IBC/IRC",
  },
  MS: {
    ibc: "2021 IBC",
    irc: "2021 IRC",
    iecc: "2015 IECC",
    ipc: "2021 IPC",
    imc: "2021 IMC",
    notes: "Mississippi energy code lags — verify locally",
  },
  MO: {
    ibc: "2018 IBC",
    irc: "Varies by jurisdiction",
    iecc: "2009 IECC",
    ipc: "2018 IPC",
    imc: "2018 IMC",
    notes: "Missouri has no statewide residential code; energy code varies widely",
  },
  MT: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  NE: { ibc: "2018 IBC", irc: "2018 IRC", iecc: "2018 IECC", ipc: "2018 IPC", imc: "2018 IMC" },
  NV: { ibc: "2018 IBC", irc: "2018 IRC", iecc: "2018 IECC", ipc: "2018 IPC", imc: "2018 IMC" },
  NH: {
    ibc: "2009 IBC",
    irc: "2009 IRC",
    iecc: "2009 IECC",
    ipc: "2009 IPC",
    imc: "2009 IMC",
    notes: "New Hampshire code adoptions are among the oldest in the country",
  },
  NJ: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  NM: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  NY: {
    ibc: "2020 NYSBC (2015 IBC base)",
    irc: "2020 NYSRC (2015 IRC base)",
    iecc: "2020 NYSECC (2015 IECC base)",
    ipc: "2020 IPC",
    imc: "2020 IMC",
    notes: "New York has state-specific amendments",
  },
  NC: {
    ibc: "2018 NC BC (2015 IBC base)",
    irc: "2018 NC RC (2015 IRC base)",
    iecc: "2018 NC ECC (2015 IECC base)",
    ipc: "2018 NC PC",
    imc: "2018 NC MC",
    notes: "North Carolina state-specific codes with amendments",
  },
  ND: {
    ibc: "2018 IBC",
    irc: "2018 IRC",
    iecc: "2009 IECC",
    ipc: "2018 IPC",
    imc: "2018 IMC",
    notes: "North Dakota energy code is outdated — verify locally",
  },
  OH: {
    ibc: "2017 OBC (2015 IBC base)",
    irc: "2017 ORC (2015 IRC base)",
    iecc: "2017 OECC (2012 IECC base)",
    ipc: "2017 OPC",
    imc: "2017 OMC",
    notes: "Ohio codes are based on older editions — verify locally",
  },
  OK: {
    ibc: "2020 IBC",
    irc: "2020 IRC",
    iecc: "2016 IECC",
    ipc: "2020 IPC",
    imc: "2020 IMC",
  },
  OR: {
    ibc: "2021 OSSC (2021 IBC base)",
    irc: "2021 ORSC (2021 IRC base)",
    iecc: "2021 OEESC (2021 IECC base)",
    ipc: "2021 OPC",
    imc: "2021 OMC",
    notes: "Oregon has its own specialty codes based on IBC/IRC",
  },
  PA: { ibc: "2018 IBC", irc: "2018 IRC", iecc: "2018 IECC", ipc: "2018 IPC", imc: "2018 IMC" },
  RI: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  SC: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  SD: {
    ibc: "2021 IBC",
    irc: "2021 IRC",
    iecc: "No statewide residential energy code",
    ipc: "2021 IPC",
    imc: "2021 IMC",
  },
  TN: { ibc: "2018 IBC", irc: "2018 IRC", iecc: "2018 IECC", ipc: "2018 IPC", imc: "2018 IMC" },
  TX: {
    ibc: "Varies by jurisdiction",
    irc: "2015 IRC (TDHCA residential)",
    iecc: "2021 IECC (commercial); 2015 IECC (residential)",
    ipc: "Varies",
    imc: "Varies",
    notes:
      "Texas has no statewide commercial building code; residential energy code applies to state-funded housing",
  },
  UT: { ibc: "2021 IBC", irc: "2021 IRC", iecc: "2021 IECC", ipc: "2021 IPC", imc: "2021 IMC" },
  VT: {
    ibc: "2015 IBC",
    irc: "2015 IRC",
    iecc: "RBES 5th Ed. (2015 IECC base)",
    ipc: "2015 IPC",
    imc: "2015 IMC",
    notes: "Vermont Residential Building Energy Standards",
  },
  VA: {
    ibc: "2021 USBC (2018 IBC base)",
    irc: "2021 USBC Res. (2018 IRC base)",
    iecc: "2021 VECC (2018 IECC base)",
    ipc: "2021 USBC Plumbing",
    imc: "2021 USBC Mechanical",
    notes: "Virginia Uniform Statewide Building Code — state-specific amendments apply",
  },
  WA: {
    ibc: "2021 IBC",
    irc: "2021 IRC",
    iecc: "2021 WSEC (2021 IECC base)",
    ipc: "2021 IPC",
    imc: "2021 IMC",
    notes: "Washington State Energy Code; local amendments common",
  },
  WV: {
    ibc: "2018 IBC",
    irc: "2018 IRC",
    iecc: "2015 IECC",
    ipc: "2018 IPC",
    imc: "2018 IMC",
  },
  WI: {
    ibc: "2015 IBC",
    irc: "2015 IRC",
    iecc: "2015 IECC",
    ipc: "2015 IPC",
    imc: "2015 IMC",
    notes: "Wisconsin codes based on older editions — verify locally",
  },
  WY: {
    ibc: "2021 IBC",
    irc: "2021 IRC",
    iecc: "2009 IECC (no residential mandate)",
    ipc: "2021 IPC",
    imc: "2021 IMC",
    notes: "Wyoming has no statewide residential energy code requirement",
  },
};

// ---------------------------------------------------------------------------
// IECC prescriptive envelope values — 2021 IECC Table R402.1.2
// ---------------------------------------------------------------------------
export const IECC_ENVELOPE: Record<string, IECCEnvelopeRow> = {
  "1A": {
    fenestrationU: "0.40",
    skylightU: "0.75",
    shgc: "0.25",
    ceilingR: "R-30",
    wfWallR: "R-13",
    massWallR: "R-3 ci",
    floorR: "R-13",
    basementR: "NR",
    slabR: "NR",
    crawlR: "NR",
  },
  "2A": {
    fenestrationU: "0.40",
    skylightU: "0.65",
    shgc: "0.25",
    ceilingR: "R-38",
    wfWallR: "R-13",
    massWallR: "R-4 ci",
    floorR: "R-13",
    basementR: "NR",
    slabR: "NR",
    crawlR: "NR",
  },
  "2B": {
    fenestrationU: "0.40",
    skylightU: "0.65",
    shgc: "NR",
    ceilingR: "R-38",
    wfWallR: "R-13",
    massWallR: "R-4 ci",
    floorR: "R-13",
    basementR: "NR",
    slabR: "NR",
    crawlR: "NR",
  },
  "3A": {
    fenestrationU: "0.35",
    skylightU: "0.55",
    shgc: "0.25",
    ceilingR: "R-38",
    wfWallR: "R-20 or R-13+5 ci",
    massWallR: "R-8 ci",
    floorR: "R-19",
    basementR: "R-5/13",
    slabR: "NR",
    crawlR: "R-5/13",
  },
  "3B": {
    fenestrationU: "0.35",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-38",
    wfWallR: "R-20 or R-13+5 ci",
    massWallR: "R-8 ci",
    floorR: "R-19",
    basementR: "R-5/13",
    slabR: "NR",
    crawlR: "R-5/13",
  },
  "3C": {
    fenestrationU: "0.35",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-38",
    wfWallR: "R-20 or R-13+5 ci",
    massWallR: "R-8 ci",
    floorR: "R-19",
    basementR: "R-5/13",
    slabR: "NR",
    crawlR: "R-5/13",
  },
  "4A": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "0.40",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-8 ci",
    floorR: "R-19",
    basementR: "R-10/13",
    slabR: "R-10, 2 ft",
    crawlR: "R-10/13",
  },
  "4B": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-8 ci",
    floorR: "R-19",
    basementR: "R-10/13",
    slabR: "R-10, 2 ft",
    crawlR: "R-10/13",
  },
  "4C": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-8 ci",
    floorR: "R-19",
    basementR: "R-10/13",
    slabR: "R-10, 2 ft",
    crawlR: "R-10/13",
  },
  "5A": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-13 ci",
    floorR: "R-30",
    basementR: "R-10/13",
    slabR: "R-10, 4 ft",
    crawlR: "R-10/13",
  },
  "5B": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-13 ci",
    floorR: "R-30",
    basementR: "R-10/13",
    slabR: "R-10, 4 ft",
    crawlR: "R-10/13",
  },
  "5C": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-13 ci",
    floorR: "R-30",
    basementR: "R-10/13",
    slabR: "R-10, 4 ft",
    crawlR: "R-10/13",
  },
  "6A": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-15 ci",
    floorR: "R-30",
    basementR: "R-10/13",
    slabR: "R-10, 4 ft",
    crawlR: "R-10/13",
  },
  "6B": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-15 ci",
    floorR: "R-30",
    basementR: "R-10/13",
    slabR: "R-10, 4 ft",
    crawlR: "R-10/13",
  },
  "7": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-19 ci",
    floorR: "R-38",
    basementR: "R-10/13",
    slabR: "R-10, 4 ft",
    crawlR: "R-10/13",
  },
  "8": {
    fenestrationU: "0.32",
    skylightU: "0.55",
    shgc: "NR",
    ceilingR: "R-49",
    wfWallR: "R-20+5 ci or R-13+10 ci",
    massWallR: "R-19 ci",
    floorR: "R-38",
    basementR: "R-10/13",
    slabR: "R-10, 4 ft",
    crawlR: "R-10/13",
  },
};

// ---------------------------------------------------------------------------
// getClimateZone
// Normalizes county name, checks county overrides, falls back to state default.
// ---------------------------------------------------------------------------
export function getClimateZone(stateCode: string, countyName: string): string {
  const state = stateCode.toUpperCase();

  // Normalize: lowercase, strip leading "County of"/"Parish of"
  let normalized = countyName.toLowerCase().trim();
  normalized = normalized.replace(/^(county of|parish of)\s+/i, "").trim();

  // Strip trailing administrative suffixes
  normalized = normalized
    .replace(
      /\s+(county|parish|borough|municipality|census area|city and borough|unified government|metropolitan government|consolidated government)$/i,
      ""
    )
    .trim();

  // Check county-level override
  const stateOverrides = COUNTY_CZ_OVERRIDES[state];
  if (stateOverrides) {
    const override = stateOverrides[normalized];
    if (override) return override;
  }

  // Fall back to state default
  return STATE_DEFAULT_CZ[state] ?? "Unknown";
}
